import type { TokenRequest } from "ably"
import type { EntityChange } from "./model"
import { Operation } from "./model"
import type { Pages } from "./pages"
import { patchRow, removeRow } from "./pages"
import subscribe from "./subscribe"
import type { UpdateableEntity, Uuid } from "@/utils"

/** How often to check whether a fetch in flight has landed. */
export const BUSY_RETRY_MS = 250

export type Recipe<TItem> = (pages: Pages<TItem>) => void

export type SyncTools<TItem> = {
  /** The cached pages, and whether any fetch of them is in flight. */
  getCache: () => { pages: Pages<TItem> | undefined; isFetching: boolean }
  /** Applies a change now, and again once a fetch in flight has landed. */
  apply: (recipe: Recipe<TItem>) => void
  /** Applies queued changes straight away; call it right after a fetch you started lands. */
  flushReplay: () => void
  isRemoved: () => boolean
  /** Runs on cache-entry removal, e.g. to clear timers; registering one again is a no-op. */
  onRemoved: (cleanup: () => void) => void
}

type Args<TItem extends UpdateableEntity> = {
  channel: string
  event: string
  getAblyToken: () => Promise<TokenRequest>
  /** Whether a change concerns this list at all, e.g. a run of the same prompt. */
  isRelevant: (row: TItem) => boolean
  getCache: SyncTools<TItem>["getCache"]
  update: (recipe: Recipe<TItem>) => void
  removed: Promise<unknown>
  /** Gets each relevant new row; the core never places one itself. */
  onInsert: (row: TItem, actorId: Uuid | null, tools: SyncTools<TItem>) => void
}

/**
 * Keeps the loaded rows of an infinite list live from the realtime channel,
 * for an RTK Query `onCacheEntryAdded`. Resolves once the cache entry is removed.
 */
export default async function syncPages<TItem extends UpdateableEntity>({
  channel,
  event,
  getAblyToken,
  isRelevant,
  getCache,
  update,
  removed,
  onInsert,
}: Args<TItem>) {
  let replayTimer: ReturnType<typeof setTimeout> | undefined
  // A fetch lands on the pages as they were when it started, undoing changes
  // made meanwhile, so those are kept to apply again.
  let replay: Recipe<TItem>[] = []
  let isRemoved = false
  const cleanups = new Set<() => void>()

  const flushReplay = () => {
    const recipes = replay
    replay = []
    if (recipes.length) {
      update((pages) => {
        for (const recipe of recipes) {
          recipe(pages)
        }
      })
    }
  }

  const replayWhenIdle = () => {
    replayTimer = undefined
    if (isRemoved) {
      return
    }
    if (getCache().isFetching) {
      replayTimer = setTimeout(replayWhenIdle, BUSY_RETRY_MS)

      return
    }

    flushReplay()
  }

  const apply = (recipe: Recipe<TItem>) => {
    update(recipe)
    if (getCache().isFetching) {
      replay.push(recipe)
      replayTimer ??= setTimeout(replayWhenIdle, BUSY_RETRY_MS)
    }
  }

  const tools: SyncTools<TItem> = {
    getCache,
    apply,
    flushReplay,
    isRemoved: () => isRemoved,
    onRemoved: (cleanup) => {
      cleanups.add(cleanup)
    },
  }

  const unbind = await subscribe(channel, getAblyToken, {
    [event]: (change: EntityChange<TItem>) => {
      switch (change.operation) {
        case Operation.Update:
          if (isRelevant(change.payload)) {
            apply((pages) => {
              patchRow(pages, change.payload)
            })
          }

          break
        case Operation.Delete:
          apply((pages) => {
            removeRow(pages, change.payload)
          })

          break
        case Operation.Insert:
          if (isRelevant(change.payload)) {
            onInsert(change.payload, change.actorId, tools)
          }

          break
      }
    },
  })

  await removed
  isRemoved = true
  clearTimeout(replayTimer)
  cleanups.forEach((cleanup) => {
    cleanup()
  })
  unbind()
}
